// Production phases by uuid.
//
// A phase is a row on the backend (`ProjectPhase`) with a uuid. Every batch
// carries `phase_uuid` beside its `phase` name, and every project carries
// `project_phases: [{ uuid, name, order }]` beside its `phases` names. The app
// identifies a phase by its uuid: the quantities matrix, the viewer filter, the
// logistics and report trees, the rack-load selector. The name is what is shown.
//
// Stored JSON keyed by phase uses the uuid too, with no name fallback:
//   - meta.quantities.{assd,modd}_{phase,batch}_counts[].phase
//   - meta.applied_phase_templates  { [phase uuid]: template name }
//   - meta.pbtree                   { [phase uuid]: { [batch]: { [design]: n } } }
// Keys saved by name before were rewritten by the backend migration
// `companies/0038_phase_json_uuid_keys`. A key that is not the uuid of one of
// the project's phases is an orphan, and is dropped when read.
//
// Mirror of `resolve_phase()` in woodwall/companies/projects/phases.py.
// Pure functions, no imports: tests/unit/phase_keys.test.js runs them in node.

export function phaseKey(uuid) {
    return typeof uuid === "string" ? uuid.toLowerCase() : uuid
}

// The project's phases as `[{ uuid, name, order }]`, in order.
export function projectPhases(project) {
    return Array.isArray(project?.project_phases) ? project.project_phases : []
}

export function phaseResolver(project) {
    const phases = projectPhases(project)
    const by_uuid = new Map(phases.map(p => [phaseKey(p.uuid), p]))
    const resolve = key => by_uuid.get(phaseKey(key)) || null
    return {
        phases,
        resolve,
        has: key => by_uuid.has(phaseKey(key)),
        // The name to show for a phase uuid, or "" when it names no phase.
        nameOf: key => resolve(key)?.name ?? "",
        // The uuid of the phase with this name, for the few inputs that are
        // names on purpose (a typed name, a name-uniqueness check).
        uuidOfName: name => phases.find(p => p.name === name)?.uuid ?? null,
    }
}

export function resolvePhase(project, key) {
    return phaseResolver(project).resolve(key)
}

export function phaseName(project, key) {
    return phaseResolver(project).nameOf(key)
}

// Position of a phase in its project, for sorting; unknown phases go last.
export function phaseOrder(project, key) {
    return resolvePhase(project, key)?.order ?? Number.MAX_SAFE_INTEGER
}

// --- meta.quantities count rows ----------------------------------------------

function rowKey(row) {
    return `${row.design}|${phaseKey(row.phase) ?? ""}|${row.batch ?? ""}`
}

// Stored rows of one count array, as the matrix keeps them in memory: rows of
// a phase the project has, one per cell. Orphan rows (a phase deleted, or a key
// that is not a phase uuid) are dropped, so the next save leaves them out.
export function countsFromStored(rows, project) {
    if (!Array.isArray(rows)) return []
    const resolver = phaseResolver(project)
    const out = new Map()
    for (const row of rows) {
        if (!row || typeof row !== "object" || !resolver.has(row.phase)) continue
        const kept = { ...row, phase: resolver.resolve(row.phase).uuid }
        const key = rowKey(kept)
        if (!out.has(key)) out.set(key, kept)
    }
    return [...out.values()]
}

// --- Keyed objects (applied_phase_templates, pbtree) -------------------------

// { [phase uuid]: value } without the keys that name no phase of the project.
export function keyedFromStored(obj, project) {
    if (!obj || typeof obj !== "object") return {}
    const resolver = phaseResolver(project)
    const out = {}
    for (const [key, value] of Object.entries(obj)) {
        const phase = resolver.resolve(key)
        if (phase) out[phase.uuid] = value
    }
    return out
}

// `meta.pbtree` without the keys that name no phase of the project.
export function pbtreeFromStored(pbtree, project) {
    return keyedFromStored(pbtree, project)
}

// A project payload for a PUT that is not about phases. A whole project object
// copied into a form carries `phases`, and the backend treats a `phases` list as
// the new set (adding, reordering and deleting by name): sent from a stale copy,
// it would undo a rename made meanwhile. Phases change through their own
// endpoints (stores/selected.js), so these writers leave them out.
export function withoutPhases(payload) {
    if (!payload || typeof payload !== "object") return payload
    // eslint-disable-next-line no-unused-vars
    const { phases, project_phases, ...rest } = payload
    return rest
}
